import type { paths as kaPaths } from "@securityjourney/svc-report/dist/gen/knowledge-assessments.d.ts";

import {
  OpenApiFetchWrapper,
  errorMessage,
  type RpcStatus,
  type ServiceResponse,
} from "../openApiFetchWrapper";
import type {
  AssessmentRequest,
  AssessmentScores,
  KnowledgeAssessmentScoreReport,
} from "./reportTypes";

type KaScoresQuery = NonNullable<
  kaPaths["/svc/report/{tenant}/ka/scores"]["get"]["parameters"]["query"]
>;

let instance: KnowledgeAssessmentReportClient | null = null;

export function getKnowledgeAssessmentReportClient(): KnowledgeAssessmentReportClient {
  if (!instance) {
    instance = new KnowledgeAssessmentReportClient();
  }
  return instance;
}

export class KnowledgeAssessmentReportClient extends OpenApiFetchWrapper<kaPaths> {
  constructor() {
    super();
  }

  async getScores(
    tenant: string,
    req: AssessmentRequest
  ): Promise<ServiceResponse<AssessmentScores>> {
    const queryParams = buildKaQueryParams(req);
    try {
      const response = await this.client.GET("/svc/report/{tenant}/ka/scores", {
        params: { path: { tenant }, query: queryParams },
      });
      if (response.data) {
        return { data: correlateTopicsWithScores(response.data), error: null };
      }
      return {
        data: null,
        error: new Error(
          (response.error as RpcStatus | undefined)?.message ||
            "Failed to get knowledge assessment scores"
        ),
      };
    } catch (error) {
      return { data: null, error: new Error(errorMessage(error) || "Fetch error") };
    }
  }

  async downloadScoresCsv(tenant: string, req: AssessmentRequest): Promise<void> {
    const queryParams = buildKaQueryParams(req);
    const res = await this.client.GET("/svc/report/{tenant}/ka/scores", {
      headers: { Accept: "text/csv" },
      parseAs: "stream",
      params: { path: { tenant }, query: queryParams },
    });
    if (!res.response.ok) {
      throw new Error(`Failed to download CSV: ${res.response.status} ${res.response.statusText}`);
    }
    const filename = parseDispositionFilename(res.response) || "knowledge-assessment-scores.csv";
    const blob = await res.response.blob();
    downloadFile(blob, filename);
  }
}

function buildKaQueryParams(req: AssessmentRequest): KaScoresQuery {
  const queryParams: KaScoresQuery = {};
  if (req.assignmentId) queryParams.assignmentId = req.assignmentId;
  if (req.examKey) queryParams.examKey = req.examKey;
  if (req.userInitiated !== undefined) queryParams.userInitiated = req.userInitiated;
  if (req.filter?.completed?.start) {
    queryParams["filter.completed.start.seconds"] = Math.floor(
      new Date(req.filter.completed.start).getTime() / 1000
    );
    queryParams["filter.completed.start.nanos"] = 0;
  }
  return queryParams;
}

function correlateTopicsWithScores(data: KnowledgeAssessmentScoreReport): AssessmentScores {
  return {
    ...data,
    assignmentScores:
      data.assignmentScores?.map(assign => ({
        ...assign,
        learnerScores:
          assign.learnerScores?.map(learner => ({
            ...learner,
            scores: learner.scores.map((score, index) => {
              let topic = data.topics && data.topics[index];
              if (!topic) {
                topic = { path: "error", names: ["Unknown Topic"] };
              }
              return {
                ...(typeof score === "object" && score !== null ? score : {}),
                topic,
              };
            }),
          })) || [],
      })) || [],
  } as AssessmentScores;
}

function downloadFile(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
}

function parseDispositionFilename(response: Response): string | null {
  const disposition = response.headers.get("Content-Disposition");
  if (!disposition) return null;
  const match = /filename="([^"]+)"/.exec(disposition);
  return match?.[1] ?? null;
}
